import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataGrid from './DataGrid';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import Alert from './Alert';
import PageHeader from './PageHeader';
import Spinner from './Spinner';
import FieldHelpIcon from './FieldHelpIcon';
import ImageUploaderWithCrop from '../utils/ImageUploaderWithCrop';
import MenuService from '../../services/MenuService';
import { convertToImageUrl, addCacheBuster } from '../../utils/imageUrlHelper';
import { useNotification } from '../../contexts/NotificationContext';

/**
 * Respuesta paginada: PageImpl legacy (campos en raíz) vs Spring Data VIA_DTO (metadatos en `page`).
 */
const normalizeSpringDataPage = (result) => {
  if (!result || typeof result !== 'object') {
    return { content: [], totalPages: 1, totalElements: 0, numberOfElements: 0 };
  }
  const content = Array.isArray(result.content) ? result.content : [];
  const meta = result.page && typeof result.page === 'object' ? result.page : result;
  const totalPages = Math.max(1, (meta.totalPages ?? result.totalPages) || 1);
  const totalElements = meta.totalElements ?? result.totalElements ?? 0;
  const numberOfElements =
    result.numberOfElements ?? meta.numberOfElements ?? content.length;
  return { content, totalPages, totalElements, numberOfElements };
};

/**
 * Genera initialFormData automáticamente desde columns
 */
const generateInitialFormData = (columns) => {
  const formData = {};
  columns.forEach(col => {
    // Soportar tanto la estructura nueva (aplanada) como la antigua (anidada)
    const showInForm = col.showInForm ?? col.formConfig?.show;
    if (showInForm) {
      formData[col.key] = '';
    }
  });
  return formData;
};

/**
 * Convierte base64 a Blob para envío al backend
 */
const base64ToBlob = async (base64) => {
  try {
    const response = await fetch(base64);
    return await response.blob();
  } catch (error) {
    console.error('Error converting base64 to blob:', error);
    throw error;
  }
};

const pad2 = (n) => String(n).padStart(2, '0');

const formatAsYmd = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  return `${y}-${m}-${d}`;
};

const parseYmd = (value) => {
  if (!value || typeof value !== 'string') return null;
  const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
};

const DateFilterInput = ({ id, value, placeholder, onChange }) => {
  const selectedDate = useMemo(() => parseYmd(value), [value]);
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => selectedDate || new Date());
  const rootRef = useRef(null);
  const [popoverPos, setPopoverPos] = useState(null);

  useEffect(() => {
    if (selectedDate) setViewDate(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      const el = rootRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const updatePos = () => {
      const el = rootRef.current;
      if (!el) return;
      const input = el.querySelector('input');
      if (!input) return;
      const r = input.getBoundingClientRect();
      setPopoverPos({
        top: Math.round(r.bottom + 8),
        left: Math.round(r.left),
        width: Math.round(r.width),
      });
    };

    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [isOpen]);

  const startOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const startWeekday = startOfMonth.getDay(); // 0..6 (Sun..Sat)

  const days = useMemo(() => {
    const endOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
    const items = [];
    for (let i = 0; i < startWeekday; i += 1) items.push(null);
    for (let day = 1; day <= endOfMonth.getDate(); day += 1) {
      items.push(new Date(viewDate.getFullYear(), viewDate.getMonth(), day));
    }
    return items;
  }, [startWeekday, viewDate]);

  const monthLabel = useMemo(() => {
    try {
      return viewDate.toLocaleString(undefined, { month: 'long', year: 'numeric' });
    } catch (_) {
      return `${viewDate.getFullYear()}-${pad2(viewDate.getMonth() + 1)}`;
    }
  }, [viewDate]);

  const isSameDay = (a, b) => (
    a && b
    && a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
  );

  return (
    <div ref={rootRef} className="date-filter-root">
      <div className="filter-input-wrapper date-filter-wrapper">
        <input
          id={id}
          type="text"
          readOnly
          className="form-control date-filter-input"
          placeholder={placeholder}
          value={value || ''}
          onClick={() => setIsOpen(true)}
        />
        <button
          type="button"
          className="date-filter-button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setIsOpen((v) => !v)}
          aria-label={placeholder || 'Seleccionar'}
          title={placeholder || 'Seleccionar'}
        >
          <i className="fa fa-calendar"></i>
        </button>
      </div>

      {isOpen && (
        <div
          className="date-filter-popover"
          role="dialog"
          aria-label="Calendar"
          style={{
            top: popoverPos?.top ?? undefined,
            left: popoverPos?.left ?? undefined,
            width: 320,
          }}
        >
          <div className="date-filter-popover-header">
            <button
              type="button"
              className="date-filter-nav"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}
              aria-label="Previous month"
              title="Previous month"
            >
              ‹
            </button>
            <div className="date-filter-month">{monthLabel}</div>
            <button
              type="button"
              className="date-filter-nav"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}
              aria-label="Next month"
              title="Next month"
            >
              ›
            </button>
          </div>

          <div className="date-filter-weekdays">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d) => (
              <div key={d} className="date-filter-weekday">{d}</div>
            ))}
          </div>

          <div className="date-filter-days">
            {days.map((d, idx) => {
              if (!d) return <div key={`empty-${idx}`} className="date-filter-day is-empty" />;
              const selected = isSameDay(d, selectedDate);
              const today = isSameDay(d, new Date());
              return (
                <button
                  key={formatAsYmd(d)}
                  type="button"
                  className={`date-filter-day ${selected ? 'is-selected' : ''} ${today ? 'is-today' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange(formatAsYmd(d));
                    setIsOpen(false);
                  }}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          <div className="date-filter-popover-footer">
            <button
              type="button"
              className="btn btn-default btn-sm"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange('');
                setIsOpen(false);
              }}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(formatAsYmd(new Date()));
                setIsOpen(false);
              }}
            >
              Hoy
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * type: 'file' (HTML) + accept.
 * accept imagen => crop + preview en grid.
 * accept otro => <input type="file"> y valida ese tipo.
 */
const getColumnAccept = (col) => col.accept ?? col.formConfig?.accept;

const isImageAccept = (accept) => {
  if (accept == null || accept === '') return false;
  return String(accept).split(',').some((token) => {
    const value = token.trim().toLowerCase();
    return value === 'image' || value === 'image/*' || value.startsWith('image/');
  });
};

const isFileField = (col) => {
  const fieldType = col.type ?? col.formConfig?.type;
  return fieldType === 'file';
};

const isImageFileField = (col) => isFileField(col) && isImageAccept(getColumnAccept(col));

const getDisplayFileName = (value) => {
  if (value instanceof File) return value.name;
  if (typeof value !== 'string' || !value.trim()) return '';
  const normalized = value.replace(/\\/g, '/');
  const parts = normalized.split('/');
  return parts[parts.length - 1] || value;
};

const fileMatchesAccept = (file, accept) => {
  if (!accept || !file) return true;
  const tokens = String(accept).split(',').map((token) => token.trim().toLowerCase()).filter(Boolean);
  if (!tokens.length) return true;
  const fileName = (file.name || '').toLowerCase();
  const mime = (file.type || '').toLowerCase();
  return tokens.some((token) => {
    if (token.endsWith('/*')) return mime.startsWith(token.slice(0, -1));
    if (token.startsWith('.')) return fileName.endsWith(token);
    if (token.includes('/')) return mime === token;
    return mime === token || fileName.endsWith(`.${token}`);
  });
};

const hasFileFields = (columns) => columns.some((col) => isFileField(col));

/**
 * Construye FormData automáticamente si hay campos type: 'file'
 * Si no hay archivos, devuelve el objeto tal cual
 */
const buildFormData = async (formData, columns, isUpdate = false) => {
  if (!hasFileFields(columns)) {
    return formData;
  }

  const data = new FormData();

  for (const col of columns) {
    const showInForm = col.showInForm ?? col.formConfig?.show;
    if (!showInForm) continue;

    const fieldType = col.type ?? col.formConfig?.type;
    const fieldValue = formData[col.key];
    const backendKey = col.backendKey ?? col.key;
    const accept = getColumnAccept(col);

    if (fieldType === 'file' && isImageAccept(accept)) {
      const shouldProcessImage = isUpdate
        ? (fieldValue && typeof fieldValue === 'string' && fieldValue.startsWith('data:image/'))
        : fieldValue;

      if (shouldProcessImage && typeof fieldValue === 'string' && fieldValue.startsWith('data:image/')) {
        const imageBlob = await base64ToBlob(fieldValue);
        data.append(backendKey, imageBlob, `${backendKey}.png`);
      } else if (fieldValue instanceof File) {
        data.append(backendKey, fieldValue, fieldValue.name);
      }
    } else if (fieldType === 'file') {
      if (fieldValue instanceof File) {
        data.append(backendKey, fieldValue, fieldValue.name);
      }
    } else if (fieldType === 'select') {
      const selectBackendKey = col.backendKey ?? `${col.key}Id`;
      if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
        data.append(selectBackendKey, fieldValue);
      }
    } else if (fieldValue !== undefined && fieldValue !== null) {
      data.append(backendKey, fieldValue);
    }
  }

  if (formData.enabled !== undefined) {
    data.append('enabled', formData.enabled || false);
  }

  return data;
};

/**
 * Calcula automáticamente el ancho de columna según el tamaño relativo y total de filtros
 */
const calculateFilterWidth = (totalFilters, filterSize) => {
  // Mapeo de tamaños relativos a proporciones
  const sizeMap = {
    small: 2,
    medium: 4,
    large: 6
  };
  
  // Si se especifica un tamaño relativo
  if (filterSize && sizeMap[filterSize]) {
    return `col-md-${sizeMap[filterSize]}`;
  }
  
  // Distribución automática equitativa
  if (totalFilters === 1) return 'col-md-12';
  if (totalFilters === 2) return 'col-md-6';
  if (totalFilters === 3) return 'col-md-4';
  if (totalFilters === 4) return 'col-md-3';
  if (totalFilters === 5) return 'col-md-2';
  
  // Para 6 o más, usar 2 columnas (col-md-2)
  return 'col-md-2';
};

const getEnabledFilterDefaults = (columns, initialFilters = {}) => {
  const hasEnabledFilter = columns.some((col) => {
    const showInFilter = col.showInFilter ?? col.filterConfig?.show;
    const filterType = col.filterType ?? col.filterConfig?.type ?? col.type ?? 'text';
    const filterKey = col.filterKey ?? col.filterConfig?.filterKey ?? col.key;
    return showInFilter && filterType === 'checkbox' && (filterKey === 'enabled' || col.key === 'enabled');
  });

  if (!hasEnabledFilter) {
    return {
      initialFiltersState: { ...initialFilters },
      initialFilterStatesState: { ...initialFilters }
    };
  }

  const enabledDefault = initialFilters.enabled === undefined ? true : initialFilters.enabled;
  return {
    initialFiltersState: { ...initialFilters, enabled: enabledDefault },
    initialFilterStatesState: { ...initialFilters, enabled: enabledDefault }
  };
};

/**
 * Renderiza automáticamente filtros desde la configuración de columns
 */
const autoRenderFilters = (columns, filterStates, onFilterChange, t) => {
  // Soportar tanto estructura nueva (aplanada) como antigua (anidada)
  const filterFields = columns.filter(col => col.showInFilter ?? col.filterConfig?.show);
  
  if (filterFields.length === 0) return null;
  
  const totalFilters = filterFields.length;
  
  return (
    <div className="filter-form row">
      {filterFields.map(col => {
        // Soportar ambas estructuras
        const filterKey = col.filterKey ?? col.filterConfig?.filterKey ?? col.key;
        const filterSize = col.filterSize ?? col.filterConfig?.size;
        const filterPlaceholder = col.filterPlaceholder ?? col.filterConfig?.placeholder ?? col.placeholder;
        const filterLabel = col.filterLabel ?? col.filterConfig?.label ?? col.label;
        
        // 🎉 Traducir automáticamente si es un string de traducción
        const translatedFilterPlaceholder = typeof filterPlaceholder === 'string' && filterPlaceholder.includes('.') 
          ? t(filterPlaceholder) 
          : filterPlaceholder;
        const translatedFilterLabel = typeof filterLabel === 'string' && filterLabel.includes('.') 
          ? t(filterLabel) 
          : filterLabel;
        
        // 🎉 Cálculo automático del ancho según cantidad de filtros
        const colWidth = calculateFilterWidth(totalFilters, filterSize);
        
        // 🎉 Heredar tipo de nivel raíz si no se especifica
        const filterType = col.filterType ?? col.filterConfig?.type ?? col.type ?? 'text';
        // Para filtros, convertir tipos complejos a simples
        const normalizedType = filterType === 'textarea' ? 'text' : filterType === 'number' ? 'text' : (filterType === 'image' || filterType === 'file') ? null : filterType;
        
        // No renderizar filtros para tipos que no tienen sentido (image, password, etc)
        if (!normalizedType || normalizedType === 'password') return null;
        
        return (
          <div key={col.key} className={colWidth}>
            {normalizedType === 'checkbox' ? (
              <>
                <label htmlFor={`filter-${col.key}`} className="label-check">
                  {translatedFilterLabel}
                </label>
                <input
                  id={`filter-${col.key}`}
                  type="checkbox"
                  checked={filterStates[col.key] || false}
                  onChange={(e) => onFilterChange(col.key, filterKey, e.target.checked)}
                />
              </>
            ) : normalizedType === 'select' ? (
              <select
                id={`filter-${col.key}`}
                name={col.key}
                className="form-control"
                value={filterStates[col.key] || ''}
                onChange={(e) => onFilterChange(col.key, filterKey, e.target.value)}
                title={(col.options ?? col.formConfig?.options)?.find(opt => opt.value === filterStates[col.key])?.label || ''}
              >
                <option value="">{translatedFilterPlaceholder || t('common.all')}</option>
                {/* 🎉 Heredar options del nivel raíz o formConfig */}
                {(col.options ?? col.formConfig?.options)?.map(option => (
                  <option key={option.value} value={option.value} title={option.label}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : normalizedType === 'date' ? (
              <DateFilterInput
                id={`filter-${col.key}`}
                value={filterStates[col.key] || ''}
                placeholder={translatedFilterPlaceholder || translatedFilterLabel}
                onChange={(nextValue) => onFilterChange(col.key, filterKey, nextValue)}
              />
            ) : (
              <div className="filter-input-wrapper">
                <input
                  id={`filter-${col.key}`}
                  type="text"
                  name={col.key}
                  className="form-control"
                  placeholder={translatedFilterPlaceholder || translatedFilterLabel}
                  value={filterStates[col.key] || ''}
                  onChange={(e) => onFilterChange(col.key, filterKey, e.target.value)}
                />
                {filterStates[col.key] && (
                  <button
                    type="button"
                    className="filter-clear-button"
                    onClick={() => onFilterChange(col.key, filterKey, '')}
                    aria-label="Clear filter"
                  >
                    <i className="fa fa-times"></i>
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Valida automáticamente los campos required del formulario
 * Retorna objeto con errores por campo: { fieldKey: 'mensaje de error' }
 */
const autoValidateForm = (formData, columns, t, entityName, isEditing) => {
  const errors = {};
  
  for (const col of columns) {
    // Soportar tanto estructura nueva (aplanada) como antigua (anidada)
    const showInForm = col.showInForm ?? col.formConfig?.show;
    const requiredConfig = col.required ?? col.formConfig?.required;
    
    // 🎉 Evaluar si es una función (para requeridos condicionales)
    let required = false;
    if (typeof requiredConfig === 'function') {
      required = requiredConfig(formData, isEditing);
    } else {
      required = !!requiredConfig;
    }
    
    if (showInForm && required) {
      const value = formData[col.key];
      const missingFile = isFileField(col)
        && !(value instanceof File)
        && !(typeof value === 'string' && value.trim() !== '');
      const missingValue = !isFileField(col)
        && (!value || (typeof value === 'string' && value.trim() === ''));
      if (missingFile || missingValue) {
        // 🎉 Traducir label si es una clave de traducción
        const translatedLabel = typeof col.label === 'string' && col.label.includes('.') 
          ? t(col.label) 
          : col.label;
        
        // Intentar traducción específica, si no existe usar genérica
        const errorKey = `${entityName}.errors.${col.key}Required`;
        const genericError = `${translatedLabel} ${t('validation.isRequired') || 'es requerido'}`;
        errors[col.key] = t(errorKey, { defaultValue: genericError });
      }
    }
  }
  
  return Object.keys(errors).length > 0 ? errors : null;
};

/**
 * Renderiza automáticamente un formulario desde la configuración de columns
 */
const autoRenderForm = (
  columns,
  formData,
  onChange,
  isEditing,
  t,
  isEditImageState,
  setEditImageState,
  fieldErrors = {},
  fileLoadingByKey = {},
  onFileLoadingChange = null
) => {
  // Soportar tanto estructura nueva (aplanada) como antigua (anidada)
  const formFields = columns.filter(col => col.showInForm ?? col.formConfig?.show);
  const totalFields = formFields.length;
  
  // 🎉 Layout inteligente según cantidad de campos
  let containerClass = '';
  if (totalFields === 1) {
    containerClass = ''; // Sin grid - un solo campo ocupa todo el ancho
  } else if (totalFields >= 2 && totalFields <= 6) {
    containerClass = 'form-row-2cols'; // Grid de 2 columnas
  } else {
    containerClass = 'form-row-3cols'; // Grid de 3 columnas para muchos campos
  }
  
  return (
    <div className={containerClass}>
      {formFields.map(col => {
        const fieldId = `field-${col.key}`;
        
        // Soportar ambas estructuras (aplanada y anidada)
        const fieldType = col.type ?? col.formConfig?.type ?? 'text';
        const fieldPlaceholder = col.placeholder ?? col.formConfig?.placeholder;
        const requiredConfig = col.required ?? col.formConfig?.required;
        const fieldIcon = col.icon ?? col.formConfig?.icon;
        
        // 🎉 Evaluar si es una función (para requeridos condicionales)
        let fieldRequired = false;
        if (typeof requiredConfig === 'function') {
          fieldRequired = requiredConfig(formData, isEditing);
        } else {
          fieldRequired = !!requiredConfig;
        }
        
        const fieldFullWidth = col.fullWidth ?? col.formConfig?.fullWidth;
        const fieldDisabled = col.disabled ?? col.formConfig?.disabled;
        const fieldRows = col.rows ?? col.formConfig?.rows;
        const fieldStep = col.step ?? col.formConfig?.step;
        const fieldMin = col.min ?? col.formConfig?.min;
        const fieldOptions = col.options ?? col.formConfig?.options;
        const fieldChangeButtonText = col.changeButtonText ?? col.formConfig?.changeButtonText;
        const fieldHelp = col.help ?? col.formConfig?.help;
        
        const isDisabled = typeof fieldDisabled === 'function' ? fieldDisabled(isEditing) : fieldDisabled;
        
        // fullWidth solo tiene sentido en grids de múltiples columnas
        const fullWidthClass = (totalFields > 1 && fieldFullWidth) ? 'form-full-width' : '';
        
        // 🎉 Traducir automáticamente si es un string de traducción
        const translatedLabel = typeof col.label === 'string' && col.label.includes('.') 
          ? t(col.label) 
          : col.label;
        const translatedPlaceholder = typeof fieldPlaceholder === 'string' && fieldPlaceholder.includes('.') 
          ? t(fieldPlaceholder) 
          : fieldPlaceholder;
        const translatedChangeButtonText = typeof fieldChangeButtonText === 'string' && fieldChangeButtonText.includes('.') 
          ? t(fieldChangeButtonText) 
          : fieldChangeButtonText;
        const translatedHelp = typeof fieldHelp === 'string' && fieldHelp.trim()
          ? (fieldHelp.includes('.') ? t(fieldHelp) : fieldHelp)
          : null;
        
        return (
          <div key={col.key} className={`form-group ${fullWidthClass}`}>
            <label htmlFor={fieldId} className="crud-field-label">
              {fieldIcon && <i className={`fa ${fieldIcon}`} aria-hidden="true"></i>}
              <span>
                {translatedLabel}
                {fieldRequired && <span className="required-field" aria-label="required">*</span>}
              </span>
              {translatedHelp && <FieldHelpIcon help={translatedHelp} />}
            </label>
            
              {isImageFileField(col) ? (
              // type: 'file' + accept imagen
              <>
                {isEditing &&
                 formData[col.key] &&
                 typeof formData[col.key] === 'string' &&
                 !formData[col.key].startsWith('data:image/') &&
                 !isEditImageState ? (
                  <div className="image-preview-block">
                    <img
                      src={addCacheBuster(convertToImageUrl(formData[col.key], ''))}
                      alt=""
                      className="preview-image-modal"
                    />
                    <button 
                      type="button" 
                      onClick={() => setEditImageState(true)} 
                      className="btn btn-secondary image-preview-action"
                    >
                      {translatedChangeButtonText || t('common.changeImage')}
                    </button>
                  </div>
                ) : (
                  <ImageUploaderWithCrop 
                    onImageChange={(croppedImage) => onChange({ ...formData, [col.key]: croppedImage })} 
                  />
                )}
              </>
            ) : fieldType === 'file' ? (
              <>
                {isEditing &&
                 typeof formData[col.key] === 'string' &&
                 formData[col.key].trim() !== '' ? (
                  <div className="image-preview-block">
                    <span className="file-selected-name">{getDisplayFileName(formData[col.key])}</span>
                    <button
                      type="button"
                      onClick={() => onChange({ ...formData, [col.key]: '' })}
                      className="btn btn-secondary image-preview-action"
                      disabled={isDisabled}
                    >
                      {translatedChangeButtonText || t('common.changeFile')}
                    </button>
                  </div>
                ) : (
                  <div className="file-input-row">
                    <input
                      id={fieldId}
                      name={col.key}
                      type="file"
                      style={{ display: 'none' }}
                      {...(getColumnAccept(col) ? { accept: getColumnAccept(col) } : {})}
                      onChange={(e) => {
                        if (onFileLoadingChange) {
                          onFileLoadingChange(col.key, true);
                        }
                        const selectedFile = e.target.files && e.target.files[0] ? e.target.files[0] : '';
                        if (selectedFile && getColumnAccept(col) && !fileMatchesAccept(selectedFile, getColumnAccept(col))) {
                          e.target.value = '';
                          onChange({ ...formData, [col.key]: '' });
                          if (onFileLoadingChange) {
                            onFileLoadingChange(col.key, false);
                          }
                          return;
                        }
                        onChange({ ...formData, [col.key]: selectedFile });
                        if (!selectedFile && onFileLoadingChange) {
                          onFileLoadingChange(col.key, false);
                        }
                      }}
                      disabled={isDisabled || !!fileLoadingByKey[col.key]}
                      required={fieldRequired}
                      aria-required={fieldRequired}
                      aria-invalid={!!fieldErrors[col.key]}
                    />
                    <button
                      type="button"
                      className={`btn btn-primary ${fieldErrors[col.key] ? 'has-error' : ''}`}
                      onClick={() => {
                        if (onFileLoadingChange) {
                          onFileLoadingChange(col.key, true);
                        }
                        const input = document.getElementById(fieldId);
                        const handleWindowFocus = () => {
                          window.removeEventListener('focus', handleWindowFocus);
                          window.setTimeout(() => {
                            if (onFileLoadingChange) {
                              onFileLoadingChange(col.key, false, true);
                            }
                          }, 300);
                        };
                        window.addEventListener('focus', handleWindowFocus);
                        input?.click();
                      }}
                      disabled={isDisabled || !!fileLoadingByKey[col.key]}
                    >
                      {fileLoadingByKey[col.key] ? (
                        <>
                          <i className="fa fa-spinner fa-spin"></i> {t('common.loading')}
                        </>
                      ) : (
                        <>
                          <i className="fa fa-folder-open"></i> {t('common.selectFile')}
                        </>
                      )}
                    </button>
                    {fileLoadingByKey[col.key] && !(formData[col.key] instanceof File) && (
                      <span className="file-selected-name">
                        <i className="fa fa-spinner fa-spin"></i> {t('common.loading')}
                      </span>
                    )}
                    {!fileLoadingByKey[col.key] && formData[col.key] instanceof File && (
                      <span className="file-selected-name">{formData[col.key].name}</span>
                    )}
                  </div>
                )}
              </>
            ) : fieldType === 'textarea' ? (
              <textarea
                  id={fieldId}
                  name={col.key}
                  className={`form-control ${fieldErrors[col.key] ? 'has-error' : ''}`}
                  placeholder={translatedPlaceholder || translatedLabel}
                  value={formData[col.key] || ''}
                onChange={(e) => onChange({ ...formData, [col.key]: e.target.value })}
                rows={fieldRows || 3}
                disabled={isDisabled}
                required={fieldRequired}
                aria-required={fieldRequired}
                aria-invalid={!!fieldErrors[col.key]}
              />
            ) : fieldType === 'select' ? (
              <select
                id={fieldId}
                name={col.key}
                className={`form-control ${fieldErrors[col.key] ? 'has-error' : ''}`}
                value={formData[col.key] || ''}
                onChange={(e) => onChange({ ...formData, [col.key]: e.target.value })}
                disabled={isDisabled}
                required={fieldRequired}
                aria-required={fieldRequired}
                aria-invalid={!!fieldErrors[col.key]}
                title={fieldOptions?.find(opt => opt.value === formData[col.key])?.label || ''}
              >
                <option value="">{translatedPlaceholder || `${t('common.select')} ${translatedLabel}`}</option>
                {fieldOptions?.map(option => (
                  <option key={option.value} value={option.value} title={option.label}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                  id={fieldId}
                  name={col.key}
                  type={fieldType || 'text'}
                  className={`form-control ${fieldErrors[col.key] ? 'has-error' : ''}`}
                  placeholder={translatedPlaceholder || translatedLabel}
                  value={formData[col.key] || ''}
                onChange={(e) => onChange({ ...formData, [col.key]: e.target.value })}
                disabled={isDisabled}
                required={fieldRequired}
                aria-required={fieldRequired}
                aria-invalid={!!fieldErrors[col.key]}
                {...(fieldType === 'number' && { step: fieldStep || '0.01', min: fieldMin || '0' })}
              />
            )}
            
            {/* 🎉 Mensaje de error debajo del campo */}
            {fieldErrors[col.key] && (
              <div className="field-error-message">
                {fieldErrors[col.key]}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Componente maestro de CRUD que maneja toda la lógica de:
 * - Listado con DataGrid
 * - Paginación y sorting
 * - Modales de añadir/editar con generación automática de formularios
 * - Filtros automáticos
 * - Confirmación de borrado
 * - Mensajes de éxito/error
 * 
 * @param {object} service - Servicio con métodos getAll, create, update, delete, updateEnabled
 * @param {array} columns - Definición de columnas para DataGrid, formulario Y filtros automáticos
 *   ESTRUCTURA APLANADA (recomendada - más simple):
 *   [{
 *     key: 'name',                    // REQUERIDO - identificador del campo
 *     label: 'Nombre',                // REQUERIDO - etiqueta a mostrar
 *     sortable: true,                 // ordenable en tabla
 *     render: (row) => row.name,      // OPCIONAL - solo para casos especiales (auto-generado)
 *     
 *     // Formulario (todas OPCIONALES):
 *     showInForm: true,               // mostrar en formulario
 *     type: 'text',                   // tipo: 'text'|'textarea'|'number'|'select'|'image'|'email'|'password'
 *     placeholder: 'Enter...',        // placeholder
 *     required: true,                 // validación automática
 *     icon: 'fa-user',                // ícono FontAwesome
 *     help: 'product.helps.name',     // OPCIONAL - clave i18n (o texto). Si falta, no se muestra el ?
 *     fullWidth: true,                // ocupar ancho completo (solo si hay 2+ campos)
 *     rows: 3,                        // para textarea
 *     step: '0.01', min: '0',         // para number
 *     decimals: 2,                    // para formateo automático en grid
 *     options: [{value, label}],      // para select
 *     changeButtonText: '...',        // para image
 *     disabled: (isEditing) => ...,   // deshabilitar condicionalmente
 *     
 *     // Filtros (todas OPCIONALES - heredan de formulario):
 *     showInFilter: true,             // mostrar en filtros
 *     filterKey: 'categoryId',        // si backend espera otro nombre
 *     filterSize: 'large',            // 'small'|'medium'|'large' (auto si no se especifica)
 *     filterPlaceholder: 'All...',    // placeholder (hereda si no se especifica)
 *     filterLabel: 'Enabled'          // label personalizada para checkbox
 *   }]
 *   
 *   LAYOUT AUTOMÁTICO DEL FORMULARIO:
 *     - 1 campo: ancho completo automático (sin grid)
 *     - 2-6 campos: grid de 2 columnas
 *     - 7+ campos: grid de 3 columnas
 *   
 *   ESTRUCTURA ANIDADA (legacy - sigue funcionando):
 *   Puedes seguir usando formConfig{} y filterConfig{} si prefieres
 * @param {string} entityName - Nombre de la entidad (para traducciones)
 * @param {object} icon - Objeto de icono FontAwesome (ej: faArchive de @fortawesome/free-solid-svg-icons)
 * @param {function} renderForm - OPCIONAL: Función personalizada que renderiza el formulario (recibe {formData, onChange, isEditing})
 *                                Si no se proporciona, se genera automáticamente desde columns
 * @param {object} initialFormData - Datos iniciales del formulario (OPCIONAL si se usa formConfig en columns)
 * @param {function} validateForm - OPCIONAL: Función personalizada de validación (recibe formData, devuelve string de error o null)
 *                                  Si no se proporciona, se validan automáticamente los campos required
 * @param {function} renderFilters - OPCIONAL: Función personalizada que renderiza los filtros
 *                                   Si no se proporciona, se generan automáticamente desde columns con filterConfig
 * @param {boolean} hasEnabledToggle - OBSOLETO: Se detecta automáticamente si hay columna 'enabled'
 * @param {function} onBeforeCreate - OPCIONAL: Callback antes de crear (recibe formData, devuelve formData transformado)
 * @param {function} onBeforeUpdate - OPCIONAL: Callback antes de actualizar (recibe formData, devuelve formData transformado)
 * @param {function} onBeforeEdit - OPCIONAL: Callback antes de editar (recibe data, devuelve data transformado)
 * @param {boolean} canCreate - Si puede añadir registros (default: true)
 * @param {boolean} canEdit - Si puede editar registros (default: true)
 * @param {boolean} canDelete - Si puede borrar registros (default: true)
 * @param {boolean} canFilter - Si puede mostrar filtros (default: true)
 * @param {boolean} canRead - Si puede leer/listar registros (default: true)
 */
const CrudManager = ({
  service,
  columns,
  entityName,
  icon,
  renderForm,
  initialFormData,
  validateForm,
  renderFilters,
  onBeforeCreate,
  onBeforeUpdate,
  onBeforeEdit,
  initialFilters = {},
  // Parámetros de control de operaciones
  canCreate = true,
  canEdit = true,
  canDelete = true,
  canFilter = true,
  canRead = true,
  // Componente de formulario custom (opcional)
  customFormComponent = null,
  formLayout = 'modal',
  addRoute = null,
  editRoute = null,
  onAddClick = null,
  onEditClick = null,
  stayOnCancelInEdit = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { success: notifySuccess, error: notifyError } = useNotification();
  
  // 🎉 Detectar automáticamente si hay columna 'enabled'
  const autoHasEnabledToggle = columns.some(col => col.key === 'enabled');
  
  // Generar initialFormData automáticamente si no se proporciona
  const autoInitialFormData = initialFormData || generateInitialFormData(columns);
  
  // Estados de datos
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingOperation, setLoadingOperation] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [shouldRefreshOnClose, setShouldRefreshOnClose] = useState(false);
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [numberOfElements, setNumberOfElements] = useState(0);
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'descending' });
  
  // Filtros
  const enabledFilterDefaults = getEnabledFilterDefaults(columns, initialFilters);
  const [filters, setFilters] = useState(enabledFilterDefaults.initialFiltersState);
  const [filterStates, setFilterStates] = useState(enabledFilterDefaults.initialFilterStatesState); // Estados locales de filtros para inputs
  
  // Formulario
  const [formData, setFormData] = useState(autoInitialFormData);
  const [fileLoadingByKey, setFileLoadingByKey] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInlineFormVisible, setIsInlineFormVisible] = useState(false);
  const isInlineLayout = formLayout === 'inline';
  const showInlineForm = isInlineLayout && isInlineFormVisible;
  
  // Mensajes
  const [modalError, setModalError] = useState('');
  const [modalSuccess, setModalSuccess] = useState('');
  const [inlineSuccess, setInlineSuccess] = useState('');
  const [confirmDialogSuccess, setConfirmDialogSuccess] = useState('');
  const [confirmDialogError, setConfirmDialogError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({}); // Errores por campo
  
  // Confirmación de borrado
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Estado para el campo de imagen (si existe)
  const [isEditImageState, setEditImageState] = useState(true);
  const [originalEditData, setOriginalEditData] = useState(null);
  const [resolvedPermissions, setResolvedPermissions] = useState({
    canRead: true,
    canCreate: true,
    canUpdate: true,
    canDelete: true,
    canReport: false
  });
  const [permissionsResolved, setPermissionsResolved] = useState(false);

  const normalizeRoutePath = (pathname) => {
    if (!pathname || pathname === '/') return '/';
    let normalized = String(pathname).trim();
    if (normalized.endsWith('/')) {
      normalized = normalized.slice(0, -1);
    }
    if (normalized.endsWith('/add') || normalized.endsWith('/edit')) {
      normalized = normalized.substring(0, normalized.lastIndexOf('/'));
    }
    if (normalized.endsWith('-add') || normalized.endsWith('-edit')) {
      normalized = normalized.replace(/-(add|edit)$/i, '');
    }
    return normalized || '/';
  };

  const routeMatches = (expectedRoute, currentRoute) => {
    if (!expectedRoute || !currentRoute) return false;
    const left = normalizeRoutePath(expectedRoute);
    const right = normalizeRoutePath(currentRoute);
    return (
      right === left
      || right.startsWith(`${left}/`)
      || right.startsWith(`${left}-`)
    );
  };

  const extractRoutePermissions = (menus, currentPathname) => {
    const normalizedCurrent = normalizeRoutePath(currentPathname);
    for (const menu of Array.isArray(menus) ? menus : []) {
      const submenus = Array.isArray(menu.submenus) ? menu.submenus : [];
      for (const submenu of submenus) {
        if (routeMatches(submenu.route, normalizedCurrent)) {
          return {
            canRead: Boolean(submenu.canRead),
            canCreate: Boolean(submenu.canCreate),
            canUpdate: Boolean(submenu.canUpdate),
            canDelete: Boolean(submenu.canDelete),
            canReport: Boolean(submenu.canReport)
          };
        }
      }
      if (submenus.length === 0 && routeMatches(menu.route, normalizedCurrent)) {
        return {
          canRead: Boolean(menu.canRead),
          canCreate: Boolean(menu.canCreate),
          canUpdate: Boolean(menu.canUpdate),
          canDelete: Boolean(menu.canDelete),
          canReport: Boolean(menu.canReport)
        };
      }
    }
    return {
      canRead: false,
      canCreate: false,
      canUpdate: false,
      canDelete: false,
      canReport: false
    };
  };

  const effectiveCanRead = canRead && resolvedPermissions.canRead;
  const effectiveCanCreate = canCreate && resolvedPermissions.canCreate;
  const effectiveCanEdit = canEdit && resolvedPermissions.canUpdate;
  const effectiveCanDelete = canDelete && resolvedPermissions.canDelete;
  const effectiveCanFilter = canFilter && effectiveCanRead;

  // Cargar datos
  useEffect(() => {
    if (!permissionsResolved || !effectiveCanRead) {
      setData([]);
      setTotalPages(1);
      setTotalElements(0);
      setNumberOfElements(0);
      setIsLoading(false);
      return;
    }
    fetchData(currentPage);
  }, [currentPage, filters, sortConfig, permissionsResolved, effectiveCanRead]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadPermissions = async () => {
      setPermissionsResolved(false);
      try {
        const menus = await MenuService.getCurrentUserMenu();
        setResolvedPermissions(extractRoutePermissions(menus, location.pathname));
      } catch (_) {
        setResolvedPermissions({
          canRead: false,
          canCreate: false,
          canUpdate: false,
          canDelete: false,
          canReport: false
        });
      } finally {
        setPermissionsResolved(true);
      }
    };

    loadPermissions();

    const handleCompanyChanged = () => {
      loadPermissions();
    };

    window.addEventListener('companyChanged', handleCompanyChanged);
    return () => {
      window.removeEventListener('companyChanged', handleCompanyChanged);
    };
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escuchar cambios de empresa y recargar datos
  useEffect(() => {
    const handleCompanyChange = () => {
      // Recargar datos cuando cambia la empresa
      // Resetear a página 0 y recargar
      setCurrentPage(0);
      if (effectiveCanRead) {
        fetchData(0);
      }
    };

    window.addEventListener('companyChanged', handleCompanyChange);
    return () => {
      window.removeEventListener('companyChanged', handleCompanyChange);
    };
  }, [effectiveCanRead]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (error) {
      notifyError(`${t('common.error')}: ${error}`, { autoClose: 5000, placement: 'centered' });
      setError(null);
    }
  }, [error, notifyError, t]);

  useEffect(() => {
    if (modalError) {
      notifyError(modalError, { autoClose: 3000, placement: 'centered' });
      setModalError('');
    }
  }, [modalError, notifyError]);

  useEffect(() => {
    if (modalSuccess) {
      notifySuccess(modalSuccess, { autoClose: 3000, placement: 'centered' });
      setModalSuccess('');
    }
  }, [modalSuccess, notifySuccess]);

  useEffect(() => {
    if (inlineSuccess) {
      notifySuccess(inlineSuccess, { autoClose: 3000, placement: 'centered' });
      setInlineSuccess('');
    }
  }, [inlineSuccess, notifySuccess]);

  const fetchData = async (page) => {
    if (!effectiveCanRead) {
      setIsLoading(false);
      setLoadingOperation(false);
      return;
    }
    setLoadingOperation(true);
    try {
      const rawKey = String(sortConfig?.key ?? '').trim();
      const rawDirection = String(sortConfig?.direction ?? '').trim().toLowerCase();
      const [keyFromSort = '', dirFromSort = ''] = rawKey.split(',');
      const normalizedKey = keyFromSort.trim();
      const mergedDirection = (dirFromSort || rawDirection).trim().toLowerCase();
      const safeSortConfig = {
        key: normalizedKey && normalizedKey.toUpperCase() !== 'UNSORTED' ? normalizedKey : 'id',
        direction: mergedDirection === 'ascending' || mergedDirection === 'asc' ? 'ascending' : 'descending',
      };

      const result = await service.getAll(page, pageSize, filters, safeSortConfig);
      const pagePayload = normalizeSpringDataPage(result);
      setData(pagePayload.content);
      setTotalPages(pagePayload.totalPages);
      setTotalElements(pagePayload.totalElements);
      setNumberOfElements(pagePayload.numberOfElements);
      setIsLoading(false);
      setError(null);
    } catch (error) {
      // No mostrar error si es un error de autenticación (ya redirige al login)
      if (!error.isAuthError) {
        setError(error.message);
      }
      setIsLoading(false);
    } finally {
      setLoadingOperation(false);
    }
  };

  // Paginación
  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages && page !== currentPage) {
      setCurrentPage(page);
      setIsLoading(true);
    }
  };

  // Sorting
  const handleSort = (key) => {
    const rawKey = String(key ?? '').trim();
    const safeKey = rawKey.split(',')[0].trim();
    if (!safeKey || safeKey.toUpperCase() === 'UNSORTED') {
      setSortConfig({ key: 'id', direction: 'descending' });
      setCurrentPage(0);
      setIsLoading(true);
      return;
    }

    let direction = 'ascending';
    if (sortConfig.key === safeKey && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key: safeKey, direction });
    setCurrentPage(0);
    setIsLoading(true);
  };

  // CRUD
  const handleCreate = async () => {
    if (!effectiveCanCreate) return;
    setModalError('');
    setModalSuccess('');
    setFieldErrors({});

    // Usar validación personalizada o automática
    const validationErrors = validateForm 
      ? validateForm(formData, t)
      : autoValidateForm(formData, columns, t, entityName, false);
    
    if (validationErrors) {
      // Si es un objeto de errores por campo, usarlo
      if (typeof validationErrors === 'object') {
        setFieldErrors(validationErrors);
        setModalError(t('validation.pleaseFillRequired') || 'Por favor, complete los campos obligatorios');
      } else {
        // Si es un string (validación legacy), mostrarlo como antes
        setModalError(validationErrors);
      }
      return;
    }

    if (isFileSelectionPending || hasMissingRequiredFile) {
      setModalError(t('validation.pleaseFillRequired') || 'Por favor, complete los campos obligatorios');
      return;
    }

    setIsFormSubmitting(true);
    try {
      // 🎉 Construcción automática de FormData si hay imágenes
      let dataToSend = onBeforeCreate 
        ? await onBeforeCreate(formData)
        : await buildFormData(formData, columns, false);
      
      await service.create(dataToSend);
      setModalSuccess(t(`${entityName}.success.created`));
      setShouldRefreshOnClose(true);

      if (isInlineLayout) {
        setInlineSuccess(t(`${entityName}.success.created`));
        setFormState(autoInitialFormData);
      } else {
        setFormData(autoInitialFormData);
        setModalError('');
        setFieldErrors({});
        setEditImageState(true);
        setIsEditing(false);
        setEditId(null);
      }
    } catch (error) {
      // No mostrar error si es un error de autenticación (ya redirige al login)
      if (!error.isAuthError) {
        setModalError(error.message || 'Error creating');
        setShouldRefreshOnClose(true);
        fetchData(currentPage);
      }
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    if (!effectiveCanEdit) return;
    if (onEditClick) {
      onEditClick(item);
      return;
    }

    // Si hay formulario custom, navegar a la ruta de editar
    if (customFormComponent) {
      const parsedId = Number(item?.id);
      if (!Number.isInteger(parsedId) || parsedId <= 0) {
        setError(t('common.errorOccurred'));
        return;
      }
      const targetRoute = editRoute || `/${entityName}-edit`;
      navigate(`${targetRoute}?id=${parsedId}`);
      return;
    }
    
    // Transformación automática para edición
    let editData = item;
    if (!onBeforeEdit) {
      editData = {};
      columns.forEach(col => {
        const showInForm = col.showInForm ?? col.formConfig?.show;
        if (showInForm) {
          const fieldType = col.type ?? col.formConfig?.type;
          
          if (fieldType === 'select') {
            // Para selects, extraer el ID del objeto (ej: category.id)
            editData[col.key] = item[col.key]?.id ?? item[col.key] ?? '';
          } else {
            // Para otros campos, usar el valor directamente
            editData[col.key] = item[col.key];
          }
        }
      });
      // Preservar ID y enabled
      editData.id = item.id;
      editData.enabled = item.enabled;
    } else {
      editData = onBeforeEdit(item);
    }
    
    openEditForm(editData, item.id);
  };

  const handleUpdate = async () => {
    if (!effectiveCanEdit) return;
    setModalError('');
    setModalSuccess('');
    setFieldErrors({});

    // Usar validación personalizada o automática
    const validationErrors = validateForm 
      ? validateForm(formData, t)
      : autoValidateForm(formData, columns, t, entityName, true);
    
    if (validationErrors) {
      // Si es un objeto de errores por campo, usarlo
      if (typeof validationErrors === 'object') {
        setFieldErrors(validationErrors);
        setModalError(t('validation.pleaseFillRequired') || 'Por favor, complete los campos obligatorios');
      } else {
        // Si es un string (validación legacy), mostrarlo como antes
        setModalError(validationErrors);
      }
      return;
    }

    if (isFileSelectionPending || hasMissingRequiredFile) {
      setModalError(t('validation.pleaseFillRequired') || 'Por favor, complete los campos obligatorios');
      return;
    }

    setIsFormSubmitting(true);
    try {
      // 🎉 Construcción automática de FormData si hay imágenes
      let dataToSend = onBeforeUpdate 
        ? await onBeforeUpdate(formData, editId)
        : await buildFormData(formData, columns, true);
      
      await service.update(editId, dataToSend);
      setModalSuccess(t(`${entityName}.success.updated`));
      setShouldRefreshOnClose(true);

      if (isInlineLayout) {
        setInlineSuccess(t(`${entityName}.success.updated`));
      }
    } catch (error) {
      // No mostrar error si es un error de autenticación (ya redirige al login)
      if (!error.isAuthError) {
        setModalError(error.message || 'Error updating');
      }
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleDelete = (item) => {
    if (!effectiveCanDelete) return;
    setItemToDelete(item.id);
    setConfirmDialogSuccess('');
    setConfirmDialogError('');
    setShowConfirmDialog(true);
  };

  const closeConfirmDialog = () => {
    setShowConfirmDialog(false);
    setItemToDelete(null);
    setConfirmDialogSuccess('');
    setConfirmDialogError('');
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      setLoadingOperation(true);
      try {
        const response = await service.delete(itemToDelete);

        // Si el servicio devuelve un objeto con status false, mostrar mensaje elegante
        if (response && typeof response === 'object' && response.status === false) {
          const translatedDeleteError = t(
            `${entityName}.errors.deleteFailed`,
            {
              defaultValue: t('common.deleteFailed', {
                defaultValue: t('common.errorOccurred', {
                  defaultValue: 'No fue posible eliminar este registro.'
                })
              })
            }
          );

          setConfirmDialogSuccess('');
          setConfirmDialogError(translatedDeleteError);
        } else {
          setConfirmDialogError('');

          // Actualizar grid inmediatamente en segundo plano
          await fetchData(currentPage);

          // Cerrar modal al completar borrado exitoso, sin mensaje intermedio
          setConfirmDialogSuccess('');
          setShowConfirmDialog(false);
          setItemToDelete(null);
        }
      } catch (error) {
        // No mostrar error si es un error de autenticación (ya redirige al login)
        if (!error.isAuthError) {
          const translatedDeleteError = t(
            `${entityName}.errors.deleteFailed`,
            {
              defaultValue: t('common.deleteFailed', {
                defaultValue: t('common.errorOccurred', {
                  defaultValue: 'No fue posible eliminar este registro.'
                })
              })
            }
          );

          setConfirmDialogSuccess('');
          setConfirmDialogError(translatedDeleteError);
        }
      } finally {
        setLoadingOperation(false);
      }
    }
  };

  const handleEnabledChange = async (id, newEnabled) => {
    if (!effectiveCanEdit) return;
    setLoadingOperation(true);
    try {
      const updatedData = { enabled: newEnabled };
      await service.updateEnabled(id, updatedData);
      await fetchData(currentPage);
      setError(null);
    } catch (error) {
      // No mostrar error si es un error de autenticación (ya redirige al login)
      if (!error.isAuthError) {
        setError(error.message);
      }
    } finally {
      setLoadingOperation(false);
    }
  };

  const handleFormChange = (updatedData) => {
    setFormData(updatedData);
  };

  const handleFileLoadingChange = (fieldKey, isLoading, onlyIfEmpty = false) => {
    setFileLoadingByKey((prev) => {
      if (onlyIfEmpty) {
        const currentValue = formData[fieldKey];
        if (currentValue instanceof File) {
          return { ...prev, [fieldKey]: false };
        }
      }
      return { ...prev, [fieldKey]: isLoading };
    });
  };

  useEffect(() => {
    setFileLoadingByKey((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (next[key] && formData[key] instanceof File) {
          next[key] = false;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [formData]);

  const isFileSelectionPending = Object.values(fileLoadingByKey).some(Boolean);

  const hasMissingRequiredFile = columns.some((col) => {
    const showInForm = col.showInForm ?? col.formConfig?.show;
    if (!showInForm || !isFileField(col)) {
      return false;
    }
    const requiredConfig = col.required ?? col.formConfig?.required;
    const required = typeof requiredConfig === 'function'
      ? requiredConfig(formData, isEditing)
      : !!requiredConfig;
    if (!required) {
      return false;
    }
    const value = formData[col.key];
    return !(value instanceof File) && !(typeof value === 'string' && value.trim() !== '');
  });

  const isSubmitDisabled = isFormSubmitting || isFileSelectionPending || hasMissingRequiredFile;

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(0);
  };
  
  // Manejador de cambio de filtros automáticos
  const handleAutoFilterChange = (stateKey, filterKey, value) => {
    setFilterStates(prev => ({ ...prev, [stateKey]: value }));
    setFilters(prev => ({ ...prev, [filterKey]: value }));
    setCurrentPage(0);
  };

  const setFormState = (data) => {
    setFormData(data);
    setFileLoadingByKey({});
    setModalError('');
    setModalSuccess('');
    setFieldErrors({});
    setEditImageState(true);
  };

  const openCreateForm = () => {
    if (onAddClick) {
      onAddClick();
      return;
    }

    if (customFormComponent) {
      navigate(addRoute || `/${entityName}-add`);
      return;
    }

    setIsEditing(false);
    setEditId(null);
    setInlineSuccess('');
    setShouldRefreshOnClose(false);
    setFormState(autoInitialFormData);

    if (isInlineLayout) {
      setIsInlineFormVisible(true);
    } else {
      setIsModalOpen(true);
    }
  };

  const openEditForm = (data, id) => {
    const hasExistingImage = columns.some((col) => {
      const showInForm = col.showInForm ?? col.formConfig?.show;
      const value = data?.[col.key];
      return showInForm && isImageFileField(col) && typeof value === 'string' && value.trim() !== '';
    });

    setIsEditing(true);
    setEditId(id);
    setInlineSuccess('');
    setShouldRefreshOnClose(false);
    setOriginalEditData(data);
    setFormState(data);
    setEditImageState(!hasExistingImage);

    if (isInlineLayout) {
      setIsInlineFormVisible(true);
    } else {
      setIsModalOpen(true);
    }
  };

  const handleInlineCancel = () => {
    if (stayOnCancelInEdit && isEditing && originalEditData) {
      const restoredData = { ...originalEditData };
      setFormState(restoredData);
      const hasExistingImage = columns.some((col) => {
        const showInForm = col.showInForm ?? col.formConfig?.show;
        const value = restoredData?.[col.key];
        return showInForm && isImageFileField(col) && typeof value === 'string' && value.trim() !== '';
      });
      setEditImageState(!hasExistingImage);
      setModalError('');
      setModalSuccess('');
      setFieldErrors({});
      return;
    }

    closeForm();
  };

  const closeForm = () => {
    if (isFormSubmitting) return;

    if (shouldRefreshOnClose) {
      fetchData(currentPage);
      setShouldRefreshOnClose(false);
    }

    if (isInlineLayout) {
      setIsInlineFormVisible(false);
    } else {
      setIsModalOpen(false);
    }
    setIsEditing(false);
    setEditId(null);
    setFormData(autoInitialFormData);
    setModalError('');
    setModalSuccess('');
    setFieldErrors({});
    setEditImageState(true);
    setInlineSuccess('');
  };

  if (!permissionsResolved || isLoading) {
    return (
      <>
        <PageHeader 
          title={t(`${entityName}.title`)}
          subtitle={t(`${entityName}.subtitle`)}
          icon={icon}
        />
        <Spinner overlay={true} size="large" message={t('common.loading')} />
      </>
    );
  }

  if (!effectiveCanRead) {
    return (
      <>
        <PageHeader
          title={t(`${entityName}.title`)}
          subtitle={t(`${entityName}.subtitle`)}
          icon={icon}
        />
        <section className="panel">
          <div className="panel-body">
            <Alert
              type="error"
              message={t('common.noReadAccess', { defaultValue: 'No tienes permiso de lectura para esta pantalla.' })}
            />
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      {loadingOperation && <Spinner overlay={true} size="large" message={t('common.processing')} />}
      
      <PageHeader 
        title={showInlineForm ? t(isEditing ? `${entityName}.edit` : `${entityName}.add`) : t(`${entityName}.title`)}
        subtitle={t(`${entityName}.subtitle`)}
        icon={icon}
      >
        {showInlineForm && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={closeForm}
          >
            <i className="fa fa-arrow-left"></i>
            <span>{t('common.goBack')}</span>
          </button>
        )}
      </PageHeader>
      
      <section className="panel">
        <div className="panel-body">
          {/* Botón añadir - solo si canCreate es true */}
          {effectiveCanCreate && !showInlineForm && (
            <div className="button-container text-right">
              <button className="btn btn-primary" onClick={openCreateForm}>
                {t(`${entityName}.add`)}
              </button>
            </div>
          )}

          {/* Filtros personalizados o automáticos - solo si canFilter es true */}
          {effectiveCanFilter && !showInlineForm && (
            renderFilters 
              ? renderFilters({ filters, onFilterChange: handleFilterChange })
              : autoRenderFilters(columns, filterStates, handleAutoFilterChange, t)
          )}

          {/* DataGrid */}
          {!showInlineForm && (
            <DataGrid
              columns={columns}
                data={data}
              sortConfig={sortConfig}
              onSort={handleSort}
              onEdit={effectiveCanEdit ? handleEdit : null}
              onDelete={effectiveCanDelete ? handleDelete : null}
              hasEnabledToggle={autoHasEnabledToggle && effectiveCanEdit}
              onEnabledChange={effectiveCanEdit ? handleEnabledChange : null}
              pagination={{
                currentPage,
                totalPages,
                totalElements,
                numberOfElements
              }}
              onPageChange={handlePageChange}
            />
          )}

          {showInlineForm && (
            <>
              {renderForm 
                ? renderForm({ formData, onChange: handleFormChange, isEditing, fieldErrors })
                : autoRenderForm(columns, formData, handleFormChange, isEditing, t, isEditImageState, setEditImageState, fieldErrors, fileLoadingByKey, handleFileLoadingChange)
              }
            </>
          )}
        </div>

        {showInlineForm && (
          <div className="panel-footer" style={{ textAlign: 'right', padding: '15px' }}>
            <button
              type="button"
              className="btn btn-default"
              onClick={handleInlineCancel}
              style={{ marginRight: '10px' }}
              disabled={isFormSubmitting}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={isEditing ? handleUpdate : handleCreate}
              disabled={isSubmitDisabled}
            >
              {isFileSelectionPending ? t('common.loading') : t('common.save')}
            </button>
          </div>
        )}
      </section>

      {/* Modal de añadir/editar */}
      {!isInlineLayout && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            if (!isFormSubmitting) {
              closeForm();
            }
          }}
          title={isEditing ? t(`${entityName}.edit`) : t(`${entityName}.add`)}
          icon={icon}
        >
          {isFormSubmitting && (
            <div className="crud-modal-spinner-overlay">
              <Spinner size="medium" message={t('common.processing')} />
            </div>
          )}

          <form onSubmit={(e) => e.preventDefault()}>
            {/* Formulario personalizado o generado automáticamente */}
            {renderForm 
              ? renderForm({ formData, onChange: handleFormChange, isEditing, fieldErrors })
              : autoRenderForm(columns, formData, handleFormChange, isEditing, t, isEditImageState, setEditImageState, fieldErrors, fileLoadingByKey, handleFileLoadingChange)
            }

            {/* Footer con botones */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeForm}
                disabled={isFormSubmitting}
              >
                <i className="fa fa-times"></i> {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={isEditing ? handleUpdate : handleCreate}
                disabled={isSubmitDisabled}
              >
                {isFileSelectionPending ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i> {t('common.loading')}
                  </>
                ) : (
                  <>
                    <i className="fa fa-check"></i> {isEditing ? t('common.update') : t([`${entityName}.create`, 'common.create'])}
                  </>
                )}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal de confirmación para borrar */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={closeConfirmDialog}
        onConfirm={confirmDelete}
        title={t(`${entityName}.deleteTitle`)}
        message={t(`${entityName}.deleteConfirm`)}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        icon={icon}
        type="danger"
        successMessage={confirmDialogSuccess}
        errorMessage={confirmDialogError}
        onSuccessClose={() => {
          setConfirmDialogSuccess('');
          // No cerrar la modal automáticamente para que usuario confirme manualmente
        }}
        onErrorClose={() => setConfirmDialogError('')}
        isProcessing={loadingOperation}
      />
    </>
  );
};

export default CrudManager;

